import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useLang } from '../hooks/useLang';
import { format } from 'date-fns';
import './SearchTrips.css';

const GERMAN_CITIES = ['Berlin', 'Hamburg', 'Munich', 'Frankfurt', 'Cologne', 'Stuttgart', 'Düsseldorf', 'Dortmund', 'Essen', 'Leipzig', 'Bremen', 'Dresden', 'Hanover', 'Nuremberg'];
const TUNISIAN_CITIES = ['Tunis', 'Sfax', 'Sousse', 'Monastir', 'Bizerte', 'Gabès', 'Ariana', 'Gafsa', 'Kairouan', 'Nabeul'];
const COUNTRY_FLAG = { Germany: '🇩🇪', Tunisia: '🇹🇳' };

function TripCard({ trip }) {
  const { t } = useLang();
  const usedKg = trip.total_kg - trip.available_kg;
  const capacityPct = Math.round((usedKg / trip.total_kg) * 100);
  const stars = () => {
    const r = trip.profiles?.rating || 0;
    return '★'.repeat(Math.round(r)) + '☆'.repeat(5 - Math.round(r));
  };
  const originFlag = COUNTRY_FLAG[trip.origin_country] || '🌍';
  const destFlag = COUNTRY_FLAG[trip.destination_country] || '🌍';
  const isCash = trip.payment_mode === 'cash';
  const isEither = trip.payment_mode === 'either';

  return (
    <div className="trip-card">
      <div className="tc-header">
        <div className="tc-avatar">{trip.profiles?.full_name?.[0] || '?'}</div>
        <div className="tc-info">
          <Link to={`/transporter/${trip.profiles?.id}`} className="tc-name" style={{ textDecoration: 'none', color: 'inherit' }}>
            {trip.profiles?.full_name || 'Unknown'}
          </Link>
          <div className="tc-rating">
            <span className="stars" style={{ fontSize: '12px' }}>{stars()}</span>
            <span className="tc-reviews">{trip.profiles?.total_reviews || 0} {t('transporterProfile.reviews')}</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
          {trip.profiles?.is_verified && <span className="badge badge-green">{t('common.verified')}</span>}
          {isCash && (
            <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '6px', background: 'rgba(232,180,52,0.15)', border: '1px solid rgba(232,180,52,0.35)', color: '#F5A623' }}>
              💵 Cash pickup
            </span>
          )}
          {isEither && (
            <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '6px', background: 'rgba(52,152,219,0.12)', border: '1px solid rgba(52,152,219,0.3)', color: '#5dade2' }}>
              💳 / 💵 Flexible payment
            </span>
          )}
        </div>
      </div>

      <div className="tc-route">
        <div className="tc-city">
          <div className="tc-city-name">{trip.origin_city}</div>
          <div className="tc-city-country">{originFlag} {trip.origin_country}</div>
        </div>
        <div className="tc-arrow">
          <div className="tc-arrow-line" />
          <span>✈️</span>
          <div className="tc-arrow-line" />
        </div>
        <div className="tc-city tc-city-right">
          <div className="tc-city-name">{trip.destination_city}</div>
          <div className="tc-city-country">{destFlag} {trip.destination_country}</div>
        </div>
      </div>

      <div className="tc-meta">
        <div className="tc-meta-item">
          <div className="tc-meta-val">{format(new Date(trip.departure_date), 'MMM d')}</div>
          <div className="tc-meta-label">{t('search.departs')}</div>
        </div>
        <div className="tc-meta-item">
          <div className="tc-meta-val">€{trip.price_per_kg}</div>
          <div className="tc-meta-label">{t('search.perKg')}</div>
        </div>
        <div className="tc-meta-item">
          <div className="tc-meta-val" style={{ color: trip.available_kg < 5 ? 'var(--red)' : 'inherit' }}>
            {trip.available_kg} kg
          </div>
          <div className="tc-meta-label">{t('search.available')}</div>
        </div>
      </div>

      <div className="tc-capacity-label">
        <span>{t('search.capacity')}</span>
        <span>{Math.round(capacityPct)}% {t('search.booked')}</span>
      </div>
      <div className="capacity-bar">
        <div className="capacity-fill" style={{ width: `${capacityPct}%`, background: capacityPct > 80 ? 'var(--red)' : 'var(--green)' }} />
      </div>

      <div className="tc-tags">
        {trip.is_multi_stop && <span className="tag" style={{ background: 'rgba(232,52,26,0.12)', borderColor: 'rgba(232,52,26,0.3)', color: 'var(--sand)' }}>{t('search.multiStopBadge')}</span>}
        {trip.accepts_documents && <span className="tag" style={{ background: 'rgba(76,175,125,0.1)', borderColor: 'rgba(76,175,125,0.25)', color: '#4CAF7D' }}>📄 Docs €{trip.document_price}</span>}
        {trip.min_kg > 1 && <span className="tag">⚖️ {t('search.minBooking', { kg: trip.min_kg })}</span>}
        {trip.home_pickup && <span className="tag">🏠 {t('search.homePickupTag')}</span>}
        {trip.accepts_fragile && <span className="tag">🫧 {t('search.fragileOk')}</span>}
        {trip.accepts_bulky && <span className="tag">📦 {t('search.bulkyOk')}</span>}
        {trip.offers_home_delivery && <span className="tag">🚚 Home delivery {trip.home_delivery_fee > 0 ? `+€${trip.home_delivery_fee}` : ''}</span>}
      </div>

      {isCash && (
        <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '8px', background: 'rgba(232,180,52,0.07)', border: '1px solid rgba(232,180,52,0.2)', fontSize: '12px', color: 'rgba(245,237,216,0.6)', lineHeight: '1.5' }}>
          💵 {t('search.cashPickupInfo')}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
        {trip.is_multi_stop && (
          <Link to={`/trip/${trip.id}/itinerary`} className="btn btn-ghost" style={{ flex: '0 0 auto', fontSize: '13px', padding: '10px 14px' }}>
            {t('search.viewItinerary')}
          </Link>
        )}
        <Link to={`/trip/${trip.id}`} className="btn btn-primary tc-btn" style={{ flex: 1 }}>
          {trip.available_kg === 0 ? t('search.fullyBooked') : t('search.viewBook')}
        </Link>
      </div>
    </div>
  );
}

export default function SearchTrips() {
  const { t } = useLang();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState('de_to_tn');
  const [filters, setFilters] = useState({ origin: '', destination: '', date: '', minKg: '', maxMinKg: '', pickup: false });

  const isDeToTn = direction === 'de_to_tn';
  const originCities = isDeToTn ? GERMAN_CITIES : TUNISIAN_CITIES;
  const destCities = isDeToTn ? TUNISIAN_CITIES : GERMAN_CITIES;
  const originCountry = isDeToTn ? 'Germany' : 'Tunisia';
  const destCountry = isDeToTn ? 'Tunisia' : 'Germany';

  useEffect(() => { setFilters(f => ({ ...f, origin: '', destination: '' })); }, [direction]);
  useEffect(() => { fetchTrips(); }, [direction]);

  async function fetchTrips() {
    setLoading(true);
    let query = supabase
      .from('trips')
      .select('*, is_multi_stop, accepts_documents, document_price, profiles!transporter_id(id, full_name, rating, total_reviews, is_verified)')
      .eq('status', 'active')
      .eq('origin_country', originCountry)
      .eq('destination_country', destCountry)
      .order('departure_date', { ascending: true });

    if (filters.origin) query = query.ilike('origin_city', `%${filters.origin}%`);
    if (filters.destination) query = query.ilike('destination_city', `%${filters.destination}%`);
    if (filters.date) query = query.gte('departure_date', filters.date);
    if (filters.minKg) query = query.gte('available_kg', parseFloat(filters.minKg));
    if (filters.maxMinKg) query = query.lte('min_kg', parseFloat(filters.maxMinKg));
    if (filters.pickup) query = query.eq('home_pickup', true);

    const { data, error } = await query;
    if (!error) setTrips(data || []);
    setLoading(false);
  }

  return (
    <div className="page search-page">
      <div className="container">
        <div className="search-header">
          <h1>{t('search.title')}</h1>
          <p>{t('search.subtitle')}</p>
        </div>

        <div className="direction-toggle">
          <button className={`dir-btn ${isDeToTn ? 'active' : ''}`} onClick={() => setDirection('de_to_tn')}>
            🇩🇪 Germany → 🇹🇳 Tunisia
          </button>
          <button className="dir-swap" onClick={() => setDirection(d => d === 'de_to_tn' ? 'tn_to_de' : 'de_to_tn')} title="Swap">⇄</button>
          <button className={`dir-btn ${!isDeToTn ? 'active' : ''}`} onClick={() => setDirection('tn_to_de')}>
            🇹🇳 Tunisia → 🇩🇪 Germany
          </button>
        </div>

        <form className="filters-bar" onSubmit={e => { e.preventDefault(); fetchTrips(); }}>
          <div className="filter-group">
            <label>{t('search.fromLabel', { country: originCountry })}</label>
            <select value={filters.origin} onChange={e => setFilters(f => ({ ...f, origin: e.target.value }))}>
              <option value="">{t('search.anyCity')}</option>
              {originCities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>{t('search.toLabel', { country: destCountry })}</label>
            <select value={filters.destination} onChange={e => setFilters(f => ({ ...f, destination: e.target.value }))}>
              <option value="">{t('search.anyCity')}</option>
              {destCities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>{t('search.afterDate')}</label>
            <input type="date" value={filters.date} onChange={e => setFilters(f => ({ ...f, date: e.target.value }))} />
          </div>
          <div className="filter-group">
            <label>{t('search.minKg')}</label>
            <input type="number" placeholder="e.g. 5" min="0.5" step="0.5" value={filters.minKg} onChange={e => setFilters(f => ({ ...f, minKg: e.target.value }))} />
          </div>
          <div className="filter-group">
            <label>{t('search.maxMinKg')}</label>
            <input type="number" placeholder="e.g. 5" min="0.5" step="0.5" value={filters.maxMinKg} onChange={e => setFilters(f => ({ ...f, maxMinKg: e.target.value }))} />
          </div>
          <div className="filter-group filter-check">
            <label>
              <input type="checkbox" checked={filters.pickup} onChange={e => setFilters(f => ({ ...f, pickup: e.target.checked }))} />
              {t('search.homePickupOnly')}
            </label>
          </div>
          <button type="submit" className="btn btn-primary">{t('search.searchBtn')}</button>
        </form>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
            <div className="spinner" />
          </div>
        ) : trips.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✈️</div>
            <h3>{t('search.noTrips')}</h3>
            <p>{t('search.noTripsDesc')}</p>
          </div>
        ) : (
          <>
            <div className="results-count">
              {t('search.tripsFound', { count: trips.length })}
            </div>
            <div className="trips-grid">
              {trips.map(trip => <TripCard key={trip.id} trip={trip} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}